import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import * as React from 'react';
import {
  Box,
  Column,
  Container,
  Row,
  Section,
  Stack,
} from 'src/components-v2/Layout';
import { CmsMediaSwitch } from 'src/components-v2/Media';
import { RichTextSwitch } from 'src/containers';
import { useCmsMode } from 'src/contexts/ModeContext/ModeContext';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import ThemeProvider from 'threads5/themes/theme-provider';
import type { ILeftRightSectionProps } from './LeftRightSection';
import { WithUnorderedListStyles } from 'src/containers/RichTextSwitch/UnorderedList/UnorderedList.styled';

export interface IStandardLeftRightSectionProps extends ILeftRightSectionProps {
  heading?: React.ReactNode;
}

const StandardLeftRightSection: React.FunctionComponent<
  IStandardLeftRightSectionProps
> = ({ fields, heading, sys }) => {
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });
  const bgColor = fields?.backgroundColor?.toLowerCase() || 'inherit';
  const cmsMode = useCmsMode({ mode: fields?.mode, backgroundColor: bgColor });
  const componentTheme = useComponentTheme(cmsMode);
  const background = componentTheme.palette[bgColor] || bgColor;

  // The Figma spec (677px asset / 157px gap / 539px text on the 1730px
  // canvas) maps to asset 10 / gap 2 / text 8 of 24 columns with 2-column
  // outer margins — exact at xxl. Holding those proportions below xxl
  // squeezes the text to ~390-530px, which design flagged as too condensed
  // (SITE-6097), so smaller desktops give the text column the gap's second
  // column instead, keeping the asset width, the outer margins, and the
  // block centering untouched.
  const assetColumns = { lg: 10 };
  const textColumns = { lg: 9, xxl: 8 };
  const gapOffset = { lgOffset: 1, xxlOffset: 2 };

  const isAssetLeft = fields?.layout === 'Asset left / Text right';
  const leftColumns = isAssetLeft ? assetColumns : textColumns;
  const rightColumns = isAssetLeft ? textColumns : assetColumns;

  return (
    <ThemeProvider theme={componentTheme}>
      <Section
        sx={{
          background,
        }}
      >
        {heading && heading}
        <Container {...inspectorProps({ fieldId: 'internalTitle' })}>
          <Row>
            {/* left column */}
            <Column
              xs={22}
              xsOffset={1}
              smOffset={0}
              sm={12}
              lgOffset={2}
              {...leftColumns}
            >
              {/* Exclude mobile duplicates from Markdown; desktop includes both columns. */}
              <Box
                className='hide-for-aeo'
                sx={{ display: { xs: 'block', sm: 'none' }, mb: '3rem' }}
              >
                <MediaColumn assetSys={fields?.asset?.sys} fields={fields} />
              </Box>
              {/* for desktop */}
              <Box
                sx={{ height: '100%', display: { xs: 'none', sm: 'block' } }}
              >
                <LeftColumn
                  textSys={sys}
                  assetSys={fields?.asset?.sys}
                  {...fields}
                />
              </Box>
            </Column>
            {/* right column */}
            <Column
              xs={22}
              xsOffset={1}
              sm={11}
              smOffset={1}
              {...gapOffset}
              {...rightColumns}
            >
              {/* for mobile */}
              <Box
                className='hide-for-aeo'
                sx={{ display: { xs: 'block', sm: 'none' } }}
              >
                <TextColumn textSys={sys} fields={fields} />
              </Box>
              {/* for desktop */}
              <Box
                sx={{ height: '100%', display: { xs: 'none', sm: 'block' } }}
              >
                <RightColumn
                  textSys={sys}
                  assetSys={fields?.asset?.sys}
                  {...fields}
                />
              </Box>
            </Column>
          </Row>
        </Container>
      </Section>
    </ThemeProvider>
  );
};

export default StandardLeftRightSection;

const LeftColumn = ({ layout, textSys, assetSys, ...fields }) => {
  switch (layout) {
    case 'Asset left / Text right':
      return <MediaColumn assetSys={assetSys} fields={fields} />;
    case 'Text left / Asset right':
      return <TextColumn textSys={textSys} fields={fields} />;
    default:
      return null;
  }
};

const RightColumn = ({ layout, textSys, assetSys, ...fields }) => {
  switch (layout) {
    case 'Asset left / Text right':
      return <TextColumn textSys={textSys} fields={fields} />;
    case 'Text left / Asset right':
      return <MediaColumn assetSys={assetSys} fields={fields} />;
    default:
      return null;
  }
};

const TextColumn = ({ textSys, fields }) => {
  return (
    <Stack justifyContent='center' sx={{ height: '100%' }}>
      <Section
        component='div'
        sx={{
          // we add this code to remove the trailing paragraph added by
          // contentful's rich text editor
          '& p:nth-last-of-type(1):empty': {
            display: 'none',
          },
          // we add this to remove margin from the second last child
          // so that content is actually vertically centered
          // however it's only applicable when there are more than two nodes
          ...(fields?.body?.content?.length > 2 && {
            '& a:nth-last-of-type(2), h2:nth-last-of-type(2), h3:nth-last-of-type(2), p:nth-last-of-type(2), ul:nth-last-of-type(2)':
              {
                mb: '0',
              },
          }),
          // in some cases we also have a last child that needs margin bottom removed
          '& p:last-child': {
            mb: '0',
          },
          '& .button-group': {
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: '1.8rem', sm: '2rem', lg: '2.4rem' },
          },
          '.btn': {
            marginTop: { xs: '12px', md: '24px' },
            // Stack is a column flex container (align-items: stretch). Avoid stretching
            // embedded CMS buttons to full column width; nested sx on ButtonGroup still wins.
            alignSelf: 'flex-start',
          },
          ...fields.styles,
        }}
        {...{
          bgColor: fields?.backgroundColor || 'white',
        }}
      >
        <WithUnorderedListStyles>
          <Stack direction='column' gap={{ xs: 2, md: 3 }}>
            <RichTextSwitch
              sys={textSys}
              content={fields.body}
              nodes={{
                h2: {
                  component: 'h2',
                  fieldId: 'body',
                  // we override global margin in favor the stack wrapping this component
                  sx: { margin: '0 !important' },
                  theme: 'web 3.0',
                  variant: 'Large Headline',
                },
                h3: {
                  component: 'h3',
                  fieldId: 'body',
                  // we override global margin in favor the stack wrapping this component
                  sx: { margin: '0 !important' },
                  theme: 'web 3.0',
                  variant: 'Medium Headline',
                },
                h6: {
                  className: 'eyebrow-heading',
                  component: 'h2',
                  fieldId: 'body',
                  // we override global margin in favor the stack wrapping this component
                  sx: { margin: '0 !important' },
                  theme: 'web 3.0',
                  variant: 'Eyebrow',
                },
                p: {
                  component: 'p',
                  fieldId: 'body',
                  // we override global margin in favor the stack wrapping this component
                  sx: { margin: '0 !important' },
                  theme: 'web 3.0',
                  variant: 'P1 Regular',
                },
              }}
            />
          </Stack>
        </WithUnorderedListStyles>
      </Section>
    </Stack>
  );
};

const MediaColumn = ({ fields, assetSys }) => {
  const { asset } = fields;
  return (
    <Stack
      sx={{
        height: '100%',
        justifyContent: 'center',
        '& video': {
          width: '100%',
          height: 'auto',
        },
        ...fields.styles,
      }}
    >
      <CmsMediaSwitch
        sys={assetSys}
        asset={asset?.fields}
        width={asset?.fields?.width}
        height={asset?.fields?.height}
        queryString={{
          xs: '?fm=webp&q=60&w=772',
          sm: '?fm=webp&q=60&w=1226',
          md: '?fm=webp&q=60&w=856',
          lg: `?fm=webp&q=60&w=${Math.ceil(asset?.fields?.width * 2)}`,
        }}
      />
    </Stack>
  );
};
